import type { SensitivityOrdinal } from '@dawn/feature-config';
import type * as React from 'react';
import { useEffect, useLayoutEffect, useState, useCallback } from 'react';

import { useHoverWithDebounce } from '../../hooks';
import { getIdentityColor, getSensitivityRampColor } from '../../lib/identity-palette';
import { cn } from '../../lib/utils';

const MIN_PIPE_THICKNESS_PX = 8;
const PIPE_BAR_INSET_PX = 5;

/**
 * Unambiguous edge key. Ids are opaque and unbounded — a directory group or a
 * data topic can contain any character — so a delimiter-joined key collides:
 * `A|B` -> `C` and `A` -> `B|C` both flatten to the same string, which fades the
 * wrong pipe and hands React a duplicate key.
 */
export const pipeKey = (fromId: string, toId: string): string => JSON.stringify([fromId, toId]);

export interface FlowConnection {
  fromId: string;
  toId: string;
  thickness: number;
  /** Identity index for color resolution; falls back to palette index 0 if absent. */
  fromIndex?: number;
  /** Sensitivity score (1–5) used when colorMode is 'sensitivity'. */
  sensitivityScore: SensitivityOrdinal;
}

export type FlowLayerColorMode = 'sensitivity' | 'identity';

export interface FlowLayerProps {
  connections: readonly FlowConnection[];
  identityRowRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  dataRowRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  /** Container ref so anchor coordinates are computed local to the layer. */
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * Ordered row ids — identity column followed by data column. Reordering rows
   * moves every anchor even when the connection pairs are untouched, and the
   * pairs alone cannot see it, so the order feeds the relayout signature below.
   */
  rowOrder?: readonly string[];
  /** Connection ids that should render at low opacity. Key is `pipeKey(fromId, toId)`. */
  fadedKeys?: ReadonlySet<string>;
  /** Pipes take pointer events only when this is true; a faded pipe never does. */
  isInteractive?: boolean;
  /**
   * Color resolution mode for pipe strokes.
   * - 'identity': uses identity palette (default, preserves V2 appearance)
   * - 'sensitivity': uses sensitivity ramp based on sensitivityScore
   */
  colorMode?: FlowLayerColorMode;
  onPipeHoverEnter?: (fromId: string, toId: string, anchorRect: DOMRect) => void;
  onPipeHoverLeave?: (fromId: string, toId: string) => void;
  className?: string;
}

interface BarRect {
  top: number;
  height: number;
  attachX: number;
}

interface PipeLayout {
  connection: FlowConnection;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  strokeWidth: number;
}

function getBarRect(
  rowEl: HTMLElement,
  containerEl: HTMLElement,
  kind: 'source' | 'target'
): BarRect | null {
  const containerRect = containerEl.getBoundingClientRect();
  const bar = rowEl.querySelector<HTMLElement>(`[data-flow-bar="${kind}"]`);
  const target = bar ?? rowEl;
  const r = target.getBoundingClientRect();
  return {
    top: r.top - containerRect.top,
    height: r.height,
    attachX: kind === 'source' ? r.right - containerRect.left : r.left - containerRect.left,
  };
}

interface PipeEntry {
  conn: FlowConnection;
  width: number;
  fromY: number;
  toY: number;
}

function placeAlongBar(group: PipeEntry[], rect: BarRect, kind: 'source' | 'target') {
  const sorted = [...group].sort((a, b) => b.width - a.width);
  if (sorted.length === 0) return;

  const barTop = rect.top;
  const barBottom = rect.top + rect.height;
  const barCenter = (barTop + barBottom) / 2;

  const setY = (e: PipeEntry, y: number) => {
    if (kind === 'source') e.fromY = y;
    else e.toY = y;
  };

  if (sorted.length === 1) {
    const only = sorted[0];
    if (only) setY(only, barCenter);
    return;
  }

  const topEntry = sorted[0];
  const bottomEntry = sorted[sorted.length - 1];
  if (!topEntry || !bottomEntry) return;

  const topY = barTop + PIPE_BAR_INSET_PX + topEntry.width / 2;
  const bottomY = barBottom - PIPE_BAR_INSET_PX - bottomEntry.width / 2;

  if (topY >= bottomY) {
    for (const e of sorted) setY(e, barCenter);
    return;
  }

  const last = sorted.length - 1;
  sorted.forEach((e, i) => {
    setY(e, topY + (bottomY - topY) * (i / last));
  });
}

function computeLayout(
  connections: readonly FlowConnection[],
  identityRowRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>,
  dataRowRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>,
  containerEl: HTMLElement
): PipeLayout[] {
  const entries: PipeEntry[] = connections.map((conn) => ({
    conn,
    width: Math.max(MIN_PIPE_THICKNESS_PX, conn.thickness),
    fromY: 0,
    toY: 0,
  }));

  const sourceRects: Record<string, BarRect> = {};
  const targetRects: Record<string, BarRect> = {};
  for (const e of entries) {
    if (!(e.conn.fromId in sourceRects)) {
      const el = identityRowRefs.current[e.conn.fromId];
      if (el) {
        const rect = getBarRect(el, containerEl, 'source');
        if (rect) sourceRects[e.conn.fromId] = rect;
      }
    }
    if (!(e.conn.toId in targetRects)) {
      const el = dataRowRefs.current[e.conn.toId];
      if (el) {
        const rect = getBarRect(el, containerEl, 'target');
        if (rect) targetRects[e.conn.toId] = rect;
      }
    }
  }

  const bySource: Record<string, PipeEntry[]> = {};
  const byTarget: Record<string, PipeEntry[]> = {};
  for (const e of entries) {
    (bySource[e.conn.fromId] ??= []).push(e);
    (byTarget[e.conn.toId] ??= []).push(e);
  }

  for (const [fromId, group] of Object.entries(bySource)) {
    const rect = sourceRects[fromId];
    if (rect) placeAlongBar(group, rect, 'source');
  }

  for (const [toId, group] of Object.entries(byTarget)) {
    const rect = targetRects[toId];
    if (rect) placeAlongBar(group, rect, 'target');
  }

  const layouts: PipeLayout[] = [];
  for (const e of entries) {
    const sRect = sourceRects[e.conn.fromId];
    const tRect = targetRects[e.conn.toId];
    if (!sRect || !tRect) continue;
    layouts.push({
      connection: e.conn,
      fromX: sRect.attachX,
      fromY: e.fromY,
      toX: tRect.attachX,
      toY: e.toY,
      strokeWidth: e.width,
    });
  }
  return layouts;
}

/** Default-mode opacity when no focus/hover is active. */
const PIPE_OPACITY_DEFAULT = 0.4;
/** Highlighted pipe opacity when focus/hover narrows the chart. Same as default
 * so dark colors don't oversaturate; contrast comes from the deep fade below. */
const PIPE_OPACITY_FOCUSED = 0.4;
/** Dimmed pipe opacity for non-related pipes during focus/hover. */
const PIPE_OPACITY_FADED = 0.05;

interface PipeProps {
  layout: PipeLayout;
  isFaded: boolean;
  hasFocus: boolean;
  isInteractive: boolean;
  colorMode: FlowLayerColorMode;
  onPipeHoverEnter?: (fromId: string, toId: string, anchorRect: DOMRect) => void;
  onPipeHoverLeave?: (fromId: string, toId: string) => void;
}

function Pipe({
  layout,
  isFaded,
  hasFocus,
  isInteractive,
  colorMode,
  onPipeHoverEnter,
  onPipeHoverLeave,
}: PipeProps) {
  const hover = useHoverWithDebounce();
  const { connection, fromX, fromY, toX, toY, strokeWidth } = layout;
  const color =
    colorMode === 'sensitivity'
      ? getSensitivityRampColor(connection.sensitivityScore)
      : getIdentityColor(connection.fromIndex ?? 0);

  const span = toX - fromX;
  const cp1X = fromX + span * 0.35;
  const cp2X = fromX + span * 0.65;
  const d = `M ${fromX},${fromY} C ${cp1X},${fromY} ${cp2X},${toY} ${toX},${toY}`;

  const handleMouseEnter = (e: React.MouseEvent<SVGPathElement>) => {
    if (!isInteractive) return;
    hover.onMouseEnter();
    if (onPipeHoverEnter) {
      const rect = new DOMRect(e.clientX, e.clientY, 0, 0);
      onPipeHoverEnter(connection.fromId, connection.toId, rect);
    }
  };

  const handleMouseLeave = () => {
    if (!isInteractive) return;
    hover.onMouseLeave();
    if (onPipeHoverLeave) {
      onPipeHoverLeave(connection.fromId, connection.toId);
    }
  };

  const strokeOpacity = isFaded
    ? PIPE_OPACITY_FADED
    : hasFocus
      ? PIPE_OPACITY_FOCUSED
      : PIPE_OPACITY_DEFAULT;

  return (
    <path
      data-testid={`pipe-${connection.fromId}-${connection.toId}`}
      d={d}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="butt"
      strokeOpacity={strokeOpacity}
      vectorEffect="non-scaling-stroke"
      style={{ pointerEvents: isInteractive && !isFaded ? 'stroke' : 'none' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );
}

export function FlowLayer({
  connections,
  identityRowRefs,
  dataRowRefs,
  containerRef,
  fadedKeys,
  isInteractive = false,
  colorMode = 'identity',
  rowOrder,
  onPipeHoverEnter,
  onPipeHoverLeave,
  className,
}: FlowLayerProps) {
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [, forceUpdate] = useState(0);

  const updateSize = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setSvgSize({ width: el.offsetWidth, height: el.offsetHeight });
    forceUpdate((n) => n + 1);
  }, [containerRef]);

  useEffect(() => {
    updateSize();
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateSize, containerRef]);

  // Row refs attach during commit, i.e. after the render that introduces a new
  // row set has already computed its layout — every lookup in that pass misses
  // and the layer draws nothing. Drill-down swaps the entire right-hand column
  // that way, so recompute once the refs exist. A refresh that merely reorders
  // rows does the same damage without touching the endpoint pairs: the anchors
  // all move while the pairs read identical, so `rowOrder` is part of the
  // signature too. The signature (not the arrays) is the dependency — the
  // parent rebuilds both on every render — and it is JSON rather than a
  // delimiter join because ids are opaque and a join collides (see `pipeKey`).
  const layoutSignature = JSON.stringify([
    connections.map((c) => pipeKey(c.fromId, c.toId)),
    rowOrder,
  ]);
  useLayoutEffect(() => {
    forceUpdate((n) => n + 1);
  }, [layoutSignature]);

  const containerEl = containerRef.current;
  const layouts = containerEl
    ? computeLayout(connections, identityRowRefs, dataRowRefs, containerEl)
    : [];

  return (
    <svg
      data-testid="flow-layer"
      className={cn(
        'pointer-events-none absolute inset-0',
        isInteractive && 'pointer-events-auto',
        className
      )}
      width={svgSize.width}
      height={svgSize.height}
      style={{ overflow: 'visible' }}
      aria-hidden="true"
    >
      {layouts.map((layout) => {
        const { fromId, toId } = layout.connection;
        const key = pipeKey(fromId, toId);
        const isFaded = fadedKeys?.has(key) === true;
        const hasFocus = (fadedKeys?.size ?? 0) > 0;
        return (
          <Pipe
            key={key}
            layout={layout}
            isFaded={isFaded}
            hasFocus={hasFocus}
            isInteractive={isInteractive}
            colorMode={colorMode}
            onPipeHoverEnter={onPipeHoverEnter}
            onPipeHoverLeave={onPipeHoverLeave}
          />
        );
      })}
    </svg>
  );
}

FlowLayer.displayName = 'FlowLayer';
